import React, { useState, useEffect, useCallback } from 'react';
import { subscriptionApi } from '../../api/services';
import SubscriptionStatus from './SubscriptionStatus';
import PricingPlans from './PricingPlans';
import TransactionHistory from './TransactionHistory';
import TopUpModal from './TopUpModal';
import Loader from '../../ui/Loader';
import { CreditCard, History, LayoutGrid, Zap } from 'lucide-react';

const Subscription = ({ isEmbedded = false }) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [plans, setPlans] = useState([]);
  const [mySub, setMySub] = useState(null);
  const [error, setError] = useState(null);
  const [topUpModalOpen, setTopUpModalOpen] = useState(false);
  const [topUpLoading, setTopUpLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statusRes, historyRes, plansRes, mySubRes] = await Promise.all([
        subscriptionApi.getStatus(),
        subscriptionApi.getHistory(),
        subscriptionApi.getPlans(),
        subscriptionApi.getMySubscription()
      ]);

      setStatus(statusRes);
      setHistory(historyRes);
      setPlans(plansRes);
      setMySub(mySubRes);
      setError(null);
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setError('Failed to load subscription data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpgrade = async (planId) => {
    try {
      setLoading(true);
      await subscriptionApi.upgradePlan(planId);
      await fetchData(); // Refresh data
      alert('Plan upgraded successfully!');
    } catch (err) {
      alert(err.message || 'Failed to upgrade plan');
    } finally {
      setLoading(false);
    }
  };

  const handleTopUp = async (messageCount) => {
    try {
      setTopUpLoading(true);
      await subscriptionApi.topUp(messageCount);
      setTopUpModalOpen(false);
      await fetchData(); // Refresh data
      alert('Top-up successful!');
    } catch (err) {
      alert(err.message || 'Failed to top-up');
    } finally {
      setTopUpLoading(false);
    }
  };

  if (loading && !status) return <Loader />;

  return (
    <div className={isEmbedded ? "animate-fade-in" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in"}>
      {/* Header Section */}
      {!isEmbedded && (
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
          <div>
            <h1 className="text-3xl font-black text-gray-900 tracking-tight">Subscription</h1>
            <p className="text-gray-500 text-sm font-medium mt-1">Manage your plan, track usage, and view billing history.</p>
          </div>
          
          <div className="flex bg-gray-100/80 p-1 rounded-2xl self-start">
            <TabButton 
              active={activeTab === 'overview'} 
              onClick={() => setActiveTab('overview')} 
              icon={LayoutGrid} 
              label="Overview" 
            />
            <TabButton 
              active={activeTab === 'plans'} 
              onClick={() => setActiveTab('plans')} 
              icon={Zap} 
              label="Plans" 
            />
            <TabButton 
              active={activeTab === 'history'} 
              onClick={() => setActiveTab('history')} 
              icon={History} 
              label="History" 
            />
          </div>
        </div>
      )}

      {isEmbedded && (
        <div className="flex justify-center mb-10">
          <div className="flex bg-gray-100/80 p-1 rounded-2xl">
            <TabButton 
              active={activeTab === 'overview'} 
              onClick={() => setActiveTab('overview')} 
              icon={LayoutGrid} 
              label="Overview" 
            />
            <TabButton 
              active={activeTab === 'plans'} 
              onClick={() => setActiveTab('plans')} 
              icon={Zap} 
              label="Plans" 
            />
            <TabButton 
              active={activeTab === 'history'} 
              onClick={() => setActiveTab('history')} 
              icon={History} 
              label="History" 
            />
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-100 text-red-600 p-4 rounded-2xl mb-8 flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <span className="text-lg">⚠️</span>
          </div>
          <p className="text-sm font-bold">{error}</p>
        </div>
      )}

      {/* Main Content Area */}
      <div className="space-y-12">
        {activeTab === 'overview' && (
          <div className="animate-slide-up">
            <SubscriptionStatus 
              status={status} 
              plan={mySub} 
              onTopUp={() => setTopUpModalOpen(true)}
            />
            <TransactionHistory history={history.slice(0, 5)} />
            <div className="mt-8 flex justify-center">
              <button 
                onClick={() => setActiveTab('history')}
                className="text-xs font-black text-primary-600 hover:text-primary-700 uppercase tracking-widest bg-primary-50 px-6 py-3 rounded-xl transition-all"
              >
                View Full Transaction History
              </button>
            </div>
          </div>
        )}

        {activeTab === 'plans' && (
          <div className="animate-slide-up">
            <PricingPlans 
              plans={plans} 
              onUpgrade={handleUpgrade} 
              currentPlanId={mySub?.plan?._id}
            />
          </div>
        )}

        {activeTab === 'history' && (
          <div className="animate-slide-up">
            <TransactionHistory history={history} />
          </div>
        )}
      </div>

      <TopUpModal 
        isOpen={topUpModalOpen} 
        onClose={() => setTopUpModalOpen(false)}
        onConfirm={handleTopUp}
        loading={topUpLoading}
      />
    </div>
  );
};

const TabButton = ({ active, onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${
      active 
      ? 'bg-white text-primary-600 shadow-sm' 
      : 'text-gray-500 hover:text-gray-700'
    }`}
  >
    <Icon className={`h-3.5 w-3.5 ${active ? 'text-primary-600' : 'text-gray-400'}`} />
    {label}
  </button>
);

export default Subscription;
